import type { ActionContext } from 'vuex';
import type { Configuration } from '../interfaces/configuration';
import type { PipelineStage } from '../interfaces/opportunity';
import type { CrmState, PipelineState } from './types';

const STORAGE_KEY = 'aq-crm-selected-pipeline';

function readStoredPipelineId(): string | null {
    try {
        return localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeStoredPipelineId(pipelineId: string): void {
    try {
        localStorage.setItem(STORAGE_KEY, pipelineId);
    } catch {
        // Not remembered this time; the in-memory selection still works for the session.
    }
}

const defaultState = (): PipelineState => ({
    stages: [],
    selectedPipelineId: null,
});

const mutations = {
    SET_STAGES(state: PipelineState, stages: PipelineStage[]): void {
        state.stages = stages;
    },
    SET_SELECTED_PIPELINE(state: PipelineState, pipelineId: string | null): void {
        state.selectedPipelineId = pipelineId;
        if (pipelineId) {
            writeStoredPipelineId(pipelineId);
        }
    },
};

type Context = ActionContext<PipelineState, unknown>;

const actions = {
    async initPipelines({ commit, dispatch, getters, state }: Context): Promise<void> {
        await dispatch('configurationCrm/fetchConfigurations', { force: true });

        const pipelines = getters.pipelines as Configuration[];
        const isValid = (id: string | null): id is string =>
            !!id && pipelines.some((pipeline) => pipeline.id === id);
        const candidates = [
            state.selectedPipelineId,
            readStoredPipelineId(),
            getters.defaultPipelineId as string | null,
        ];
        const next = candidates.find(isValid) ?? pipelines[0]?.id ?? null;

        commit('SET_SELECTED_PIPELINE', next);
    },
    selectPipeline({ commit }: Context, pipelineId: string): void {
        commit('SET_SELECTED_PIPELINE', pipelineId);
    },
};

const getters = {
    /** Stages sorted by their configured display order, including empty ones — the Kanban renders
     * one column per entry regardless of whether any opportunity currently sits in it. */
    stages: (state: PipelineState): PipelineStage[] => [...state.stages].sort((a, b) => a.order - b.order),
    stageById: (state: PipelineState) => (stageId: string): PipelineStage | undefined =>
        state.stages.find((stage) => stage.id === stageId),
    pipelines: (state: CrmState): Configuration[] => state.configurationCrm?.configurations ?? [],
    defaultPipelineId: (state: CrmState): string | null => state.configurationCrm?.defaultConfigurationId ?? null,
    selectedPipeline: (
        state: PipelineState,
        getters: { pipelines: Configuration[] },
    ): Configuration | null =>
        getters.pipelines.find((pipeline) => pipeline.id === state.selectedPipelineId)
        ?? getters.pipelines[0]
        ?? null,
    selectedPipelineId: (
        _state: PipelineState,
        getters: { selectedPipeline: Configuration | null },
    ): string | null => getters.selectedPipeline?.id ?? null,
};

/** Exported as plain fragments — merged with `opportunity.ts` into the single namespaced `crm`
 * module in `store/index.ts`. */
export const pipelineModule = {
    state: defaultState,
    mutations,
    actions,
    getters,
};
